// src/features/clinical/components/visit/VitalsForm.jsx
import React, { useState } from 'react';
import { FaHeart, FaLungs, FaTachometerAlt, FaThermometerHalf, FaWeight, FaRuler } from 'react-icons/fa';
import toast from 'react-hot-toast';
import clinicalApi from '../../services/clinicalApi';

const VitalsForm = ({ visitId, patientId, onComplete }) => {
  const [vitals, setVitals] = useState({
    temperature: '',
    heartRate: '',
    respiratoryRate: '',
    bloodPressureSystolic: '',
    bloodPressureDiastolic: '',
    oxygenSaturation: '',
    height: '',
    weight: '',
    painLevel: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [bmi, setBmi] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setVitals(prev => ({ ...prev, [name]: value }));
    
    // Calculate BMI when height and weight are entered
    if (name === 'height' || name === 'weight') {
      const height = name === 'height' ? parseFloat(value) : parseFloat(vitals.height);
      const weight = name === 'weight' ? parseFloat(value) : parseFloat(vitals.weight);
      
      if (height && weight && height > 0) {
        const heightInM = height / 100;
        const calculatedBmi = (weight / (heightInM * heightInM)).toFixed(1);
        setBmi(calculatedBmi);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const vitalsData = {
        ...vitals,
        temperature: parseFloat(vitals.temperature),
        heartRate: parseInt(vitals.heartRate),
        respiratoryRate: parseInt(vitals.respiratoryRate),
        bloodPressureSystolic: parseInt(vitals.bloodPressureSystolic),
        bloodPressureDiastolic: parseInt(vitals.bloodPressureDiastolic),
        oxygenSaturation: parseInt(vitals.oxygenSaturation),
        height: parseFloat(vitals.height),
        weight: parseFloat(vitals.weight),
        painLevel: parseInt(vitals.painLevel),
        bmi: parseFloat(bmi)
      };
      
      await clinicalApi.updateVitals(visitId, vitalsData);
      toast.success('Vitals recorded successfully');
      
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      toast.error('Failed to record vitals');
    } finally {
      setSubmitting(false);
    }
  };

  const getBmiCategory = (bmi) => {
    if (!bmi) return null;
    const bmiValue = parseFloat(bmi);
    if (bmiValue < 18.5) return { label: 'Underweight', color: 'text-yellow-600' };
    if (bmiValue < 25) return { label: 'Normal', color: 'text-green-600' };
    if (bmiValue < 30) return { label: 'Overweight', color: 'text-orange-600' };
    return { label: 'Obese', color: 'text-red-600' };
  };

  const bmiCategory = getBmiCategory(bmi);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Record Vital Signs</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          {/* Temperature */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaThermometerHalf className="inline mr-2 text-blue-600" />
              Temperature (°C)
            </label>
            <input
              type="number"
              name="temperature"
              step="0.1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={vitals.temperature}
              onChange={handleChange}
              placeholder="98.6"
            />
          </div>

          {/* Heart Rate */}
          <div className="bg-red-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaHeart className="inline mr-2 text-red-600" />
              Heart Rate (bpm)
            </label>
            <input
              type="number"
              name="heartRate"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={vitals.heartRate}
              onChange={handleChange}
              placeholder="72"
            />
          </div>

          {/* Respiratory Rate */}
          <div className="bg-green-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaLungs className="inline mr-2 text-green-600" />
              Respiratory Rate
            </label>
            <input
              type="number"
              name="respiratoryRate"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={vitals.respiratoryRate}
              onChange={handleChange}
              placeholder="16"
            />
          </div>

          {/* Blood Pressure */}
          <div className="bg-purple-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaTachometerAlt className="inline mr-2 text-purple-600" />
              Blood Pressure
            </label>
            <div className="flex space-x-2">
              <input
                type="number"
                name="bloodPressureSystolic"
                className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={vitals.bloodPressureSystolic}
                onChange={handleChange}
                placeholder="Systolic"
              />
              <input
                type="number"
                name="bloodPressureDiastolic"
                className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={vitals.bloodPressureDiastolic}
                onChange={handleChange}
                placeholder="Diastolic"
              />
            </div>
          </div>

          {/* Oxygen Saturation */}
          <div className="bg-indigo-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SpO₂ (%)
            </label>
            <input
              type="number"
              name="oxygenSaturation"
              min="0"
              max="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={vitals.oxygenSaturation}
              onChange={handleChange}
              placeholder="98"
            />
          </div>

          {/* Pain Level */}
          <div className="bg-orange-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pain Level (1-10)
            </label>
            <input
              type="number"
              name="painLevel"
              min="0"
              max="10"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={vitals.painLevel}
              onChange={handleChange}
              placeholder="0"
            />
          </div>

          {/* Height */}
          <div className="bg-teal-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaRuler className="inline mr-2 text-teal-600" />
              Height (cm)
            </label>
            <input
              type="number"
              name="height"
              step="0.1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={vitals.height}
              onChange={handleChange}
              placeholder="170"
            />
          </div>

          {/* Weight */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaWeight className="inline mr-2 text-yellow-600" />
              Weight (kg)
            </label>
            <input
              type="number"
              name="weight"
              step="0.1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={vitals.weight}
              onChange={handleChange}
              placeholder="70"
            />
          </div>

          {/* BMI Display */}
          {bmi && (
            <div className="bg-gray-50 p-4 rounded-lg col-span-3">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">BMI:</span>
                <span className={`text-lg font-bold ${bmiCategory?.color}`}>
                  {bmi} - {bmiCategory?.label}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            name="notes"
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={vitals.notes}
            onChange={handleChange}
            placeholder="Any additional observations..."
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onComplete}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            disabled={submitting}
          >
            Skip
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save Vitals'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VitalsForm;